// src/contexts/AuthContext.js
//
// JWT/REST auth provider that talks to al-bayan-backend.
//
// Auth flow:
//   1. Page load → POST /api/auth/refresh (uses httpOnly cookie). If 200,
//      we have an accessToken + user; else currentUser stays null.
//   2. login → POST /api/auth/login → store accessToken in memory, set user.
//   3. logout → POST /api/auth/logout → clear state + cookie.
//
// The accessToken lives in a module-level variable (NOT localStorage) and
// is re-attached to every API call via the `apiClient` helper. Refresh-on-
// 401 retry is the apiClient's job, not this provider's.

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { setCurrentTenantId } from '../services/tenantContext';
import { ROLES, resolveRole } from '../services/userService';
import { getAccessToken, setAccessToken, onAccessTokenChange } from './accessTokenStore';
import { refreshSession, onRefreshedUser } from '../services/sessionRefresh';

const API_BASE = process.env.REACT_APP_API_URL ?? '';

export { getAccessToken, setAccessToken, onAccessTokenChange };

// The boot refresh goes through the shared `refreshSession` (sessionRefresh.js)
// — the SAME coalesced in-flight promise the _apiClient on-401 path uses. This
// is what makes the whole app rotate the refresh-token cookie exactly once:
// React 18 StrictMode's double-mount, and any early data request that 401s
// before the in-memory token is set, all reuse this one refresh instead of
// firing competing rotations that would revoke each other ("Invalid token").

const apiFetch = async (path, options = {}) => {
  const headers = { 'Content-Type': 'application/json', ...(options.headers || {}) };
  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
    credentials: 'include'
  });
  let body = null;
  try {
    body = await res.json();
  } catch {
    /* non-JSON body */
  }
  if (!res.ok) {
    const err = new Error(body?.error || `Request failed: ${res.status}`);
    err.status = res.status;
    err.body = body;
    throw err;
  }
  // A 2xx with no parseable JSON usually means the request was intercepted
  // (service worker, browser extension, misrouted proxy returning HTML).
  // Surface it as a clean error rather than letting callers read `.foo` on null.
  if (body === null && res.status !== 204) {
    const err = new Error(`Unexpected empty response from ${path} (status ${res.status})`);
    err.status = res.status;
    throw err;
  }
  return body;
};

// Adapt the backend user payload to the surface AuthContext consumers
// expect (mirrors the shape Firebase produced — see commit history).
const adaptUser = (user) => {
  if (!user) return { currentUser: null, userProfile: null };
  return {
    currentUser: {
      uid: user.id || user.uid,
      email: user.email || null,
      displayName: user.displayName || null,
      phoneNumber: user.phoneNumber || null,
      photoURL: user.avatarUrl || null,
      emailVerified: !!user.emailVerified
    },
    userProfile: {
      uid: user.id || user.uid,
      id: user.id || user.uid,
      role: user.role || 'teacher',
      tenantId: user.tenantId || null,
      displayName: user.displayName || null,
      email: user.email || null,
      phone: user.phoneNumber || null,
      linkedStudentIds: user.linkedStudentIds || [],
      studentId: user.studentId || null,
      avatarUrl: user.avatarUrl || null,
      mustResetPassword: !!user.mustResetPassword,
      customClaims: user.customClaims || null
    }
  };
};

const notImplemented = (name) => () => {
  throw new Error(`${name} is not available on this auth backend.`);
};

export const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);

  const applyUser = useCallback((user) => {
    const adapted = adaptUser(user);
    setCurrentUser(adapted.currentUser);
    setUserProfile(adapted.userProfile);
    setCurrentTenantId(adapted.userProfile?.tenantId || null);
  }, []);

  useEffect(() => {
    let cancelled = false;
    // Keep the provider's user state in sync whenever ANY refresh happens —
    // including a background on-401 rotation fired by _apiClient long after
    // boot (e.g. once the 1h access token expires). refreshSession delivers
    // the fresh user payload here so role/tenant changes propagate.
    const unsubscribe = onRefreshedUser((user) => {
      if (!cancelled) applyUser(user);
    });
    (async () => {
      try {
        const data = await refreshSession();
        if (cancelled) return;
        applyUser(data.user);
      } catch {
        if (cancelled) return;
        setAccessToken(null);
        applyUser(null);
      } finally {
        if (!cancelled) setAuthReady(true);
      }
    })();
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [applyUser]);

  const login = useCallback(async (email, password) => {
    const data = await apiFetch('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password })
    });
    if (!data?.accessToken || !data?.user) {
      throw new Error('Login response missing accessToken or user');
    }
    setAccessToken(data.accessToken);
    applyUser(data.user);
    return { user: { uid: data.user.id, email: data.user.email, displayName: data.user.displayName } };
  }, [applyUser]);

  const register = useCallback(async ({ email, password, displayName, role = ROLES.PARENT }) => {
    const data = await apiFetch('/api/auth/signup', {
      method: 'POST',
      body: JSON.stringify({ email, password, displayName, role })
    });
    return { user: { uid: data.user.id, email: data.user.email, displayName: data.user.displayName } };
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiFetch('/api/auth/logout', { method: 'POST' });
    } catch {
      /* logout is best-effort */
    }
    setAccessToken(null);
    applyUser(null);
  }, [applyUser]);

  const resetPassword = useCallback(async (email) => {
    await apiFetch('/api/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email })
    });
  }, []);

  const refreshProfile = useCallback(async () => {
    const tok = getAccessToken();
    if (!tok) return;
    setProfileLoading(true);
    try {
      const data = await apiFetch('/api/auth/me', { method: 'GET' });
      applyUser(data.user);
    } catch {
      setAccessToken(null);
      applyUser(null);
    } finally {
      setProfileLoading(false);
    }
  }, [applyUser]);

  const role = resolveRole(userProfile?.role);
  const linkedStudentIds = userProfile?.linkedStudentIds || [];
  const tenantId = userProfile?.tenantId || null;

  const value = {
    currentUser,
    userProfile,
    role,
    linkedStudentIds,
    tenantId,
    authReady,
    profileLoading,
    login,
    loginWithGoogle: notImplemented('loginWithGoogle'),
    register,
    logout,
    resetPassword,
    startPhoneSignIn: notImplemented('startPhoneSignIn'),
    confirmPhoneSignIn: notImplemented('confirmPhoneSignIn'),
    refreshProfile,
    ROLES
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
